<template>
    <app-layout>
        <asset-details-header />

        <div class="items-top my-6 flex justify-between gap-2">
            <filter-bar v-model="activeFilters" :config="filterbarConfig">
                <template #results-option="{ option }"
                    ><span :class="getTaskResolutionColor(option.value, 'text')" class="font-medium">{{ option.label }}</span></template
                >
            </filter-bar>
            <export-menu
                v-if="hasAnyPermission('reference.export')"
                :active-query-params="activeFilters"
                :allowed-exports="['csv', 'xlsx', 'pdf']"
                :export-group-key="`references-${asset.id}-export`"
                :no-item-selected-tooltip="trans('general.tooltip.no_item_selected')"
                :non-query-filters="{ assetId: asset.id }"
                :selection="selection"
                routeName="tasks.export"
            />
        </div>

        <div class="py-3">
            <card>
                <template #header
                    ><h3 class="mb-0">{{ trans('references.overview.title') }}</h3>
                    <div id="references-table-actions"
                /></template>
                <references-table
                    :columns="fields"
                    :items="tasks"
                    :query-params="activeFilters"
                    :selection-enabled="hasAnyPermission('reference.export') ? `references-${asset.id}` : false"
                    pagination-container="#references-table-actions"
                />
            </card>
        </div>
    </app-layout>
</template>
<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import AssetDetailsHeader from '@/Pages/Assets/Details/AssetDetailsHeader.vue';
import Card from '@/Components/Card.vue';
import { trans } from '@/mixins/i18n.js';
import { getTaskResolutionColor } from '@/Pages/TaskList/utils.ts';
import { usePage } from '@inertiajs/vue3';
import { computed, ref } from 'vue';
import FilterBar from '@/Components/FilterBar.vue';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';
import ExportMenu from '@/Components/ExportMenu.vue';
import useSelectedItems from '@/Utils/UseSelectedItems.ts';
import ReferencesTable from '@/Components/ReferencesTable.vue';
import useReloadOnFocus from '../../../Utils/UseReloadOnFocus';

const props = defineProps({
    asset: Object,
    tasks: Object,
});

const tasksMeta = computed(() => props.tasks?.meta || {});
const selection = useSelectedItems(`references-${props.asset.id}`, tasksMeta);
useReloadOnFocus({ only: ['tasks'] });

const fields = [
    { key: 'task', label: trans('references.overview.cell.task'), sorter: false },
    { key: 'result', label: trans('references.overview.cell.result'), sorter: false },
    { key: 'date', label: trans('references.overview.cell.date'), sorter: false },
    { key: 'title', label: trans('references.overview.cell.title'), sorter: false },
    { key: 'value', label: trans('references.overview.cell.value'), sorter: false },
    { key: 'report', label: trans('references.overview.cell.report'), sorter: false },
    { key: 'user', label: trans('references.overview.cell.user'), sorter: false },
    { key: 'presence', label: 'Presence', sorter: false, _style: 'width: 40px !important' },
    { key: 'attachments', label: 'Attachments', sorter: false, _style: 'width: 40px !important' },
    { key: 'actions', sorter: false, _style: 'width: 40px !important' },
];

// activeFilters will be initialized by Filterbar component via v-model
const activeFilters = ref(null);

// Build config for the generic filterbar from page props
const page = usePage();
const filterbarConfig = {
    route: route('assets.references.index', props.asset),
    routeOptions: { only: ['tasks'] },
    defaultSort: { column: 'date', asc: false },
    additionalData: () => ({ selection: selection.getSelectionAsQueryParams() }),
    onLoadSuccess: (data) => {
        const selectionData = data.props.tasks?.meta?.selection;
        if (selectionData !== undefined) {
            selection.setSelection(selectionData);
        }
    },
    filters: [
        { key: 'search', type: 'search', placeholder: trans('references.overview.filter.search') },
        {
            key: 'taskTypes',
            type: 'multi-select',
            label: trans('tasks.overview.filter.task_type'),
            options: page.props.filters?.taskTypes?.data ?? [],
        },
        { key: 'date', type: 'date-range', label: trans('activities.overview.cell.date'), startKey: 'start', endKey: 'end' },
        {
            key: 'results',
            type: 'multi-select',
            label: trans('references.overview.filter.result'),
            options: page.props.filters?.results?.data ?? [],
        },
        {
            key: 'users',
            type: 'multi-select',
            label: trans('activities.overview.cell.user'),
            optionLabelSlot: 'truncate',
            options:
                page.props.users?.data.map(({ name, id }) => ({
                    label: name,
                    value: id,
                })) || [],
        },
    ],
};
</script>
